import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { fetchFestivalMuhurtas } from "@/lib/api";
import { PageHero } from "@/components/PageHero";
import Seo from "@/components/Seo";
import { HERO_ART } from "@/lib/heroes";
import { CalendarDays, MapPin, ChevronRight } from "lucide-react";

const prettyDate = (iso) => {
  try { return new Date(iso + "T00:00:00").toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" }); }
  catch { return iso; }
};

export default function MuhurtaIndex() {
  const { festival: fslug } = useParams();
  const { pick } = useLang();
  const [data, setData] = useState({ festivals: [], cities: [] });

  useEffect(() => { fetchFestivalMuhurtas().then(setData).catch(() => {}); }, []);

  const { festivals, cities } = data;
  const one = fslug ? festivals.find((f) => f.slug === fslug) : null;

  // Single festival → list all cities for it.
  if (fslug) {
    const name = one ? (pick(one.name_hi, one.name) || one.name) : fslug;
    return (
      <div data-testid="muhurta-festival-index">
        <Seo path={`/muhurta/${fslug}`} title={`${one?.name || "Festival"} Muhurta by City — Timings Worldwide`}
          description={`${one?.name || "Festival"} muhurta timings for major cities worldwide — Mumbai, Delhi, New York, London, Singapore and more, each computed in local time.`} />
        <PageHero art={HERO_ART.devotion} overline={pick("त्योहार मुहूर्त", "Festival Muhurta")}
          deva={one?.name_hi || "मुहूर्त"} en={name}
          tagline={one ? `${prettyDate(one.date)} · ${pick("नगर अनुसार मुहूर्त", "Muhurta by city")}` : ""} />
        <section className="mx-auto max-w-3xl px-5 py-12 md:px-8">
          <div className="grid gap-3 sm:grid-cols-2">
            {cities.map((c) => (
              <Link key={c.slug} to={`/muhurta/${fslug}/${c.slug}`} data-testid={`muhurta-city-${c.slug}`}
                className="group flex items-center justify-between border border-line bg-surface p-4 transition-colors hover:border-saffron">
                <span className="flex items-center gap-2 text-ink"><MapPin className="h-4 w-4 text-saffron" /> {c.name}{c.country ? <span className="text-sm text-ink-3">· {c.country}</span> : null}</span>
                <ChevronRight className="h-4 w-4 text-ink-3 transition-transform group-hover:translate-x-0.5 group-hover:text-saffron" />
              </Link>
            ))}
          </div>
          <Link to="/muhurta" className="mt-8 inline-block border border-line px-4 py-2 text-sm text-ink-2 hover:border-[var(--ink)] hover:text-ink">{pick("सभी त्योहार", "All festivals")}</Link>
        </section>
      </div>
    );
  }

  // All festivals.
  return (
    <div data-testid="muhurta-index">
      <Seo path="/muhurta" title="Festival Muhurta by City — Diwali, Shivaratri, Janmashtami Timings"
        description="Auspicious festival muhurta timings for cities worldwide — Diwali, Maha Shivaratri, Krishna Janmashtami, Ganesh Chaturthi, Durga Ashtami — each puja window computed live in local time." />
      <PageHero art={HERO_ART.devotion} overline={pick("नगर मुहूर्त", "City Muhurta")}
        deva="त्योहार मुहूर्त" en={pick("त्योहार मुहूर्त — नगर अनुसार", "Festival Muhurta — by City")}
        tagline={pick("प्रमुख त्योहारों के पूजा मुहूर्त — आपके नगर के स्थानीय समयानुसार।", "Puja muhurtas for major festivals — computed in your city's local time.")} />
      <section className="mx-auto max-w-3xl px-5 py-12 md:px-8">
        <div className="space-y-4">
          {festivals.map((f) => (
            <div key={f.slug} className="border border-line bg-surface p-5" data-testid={`muhurta-festival-${f.slug}`}>
              <div className="flex items-baseline justify-between gap-3">
                <Link to={`/muhurta/${f.slug}`} className="font-serif-latin text-xl text-ink hover:text-saffron">
                  {pick(f.name_hi, f.name) || f.name}
                </Link>
                <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-sm text-ink-3"><CalendarDays className="h-3.5 w-3.5" /> {prettyDate(f.date)}</span>
              </div>
              {f.significance && <p className="mt-1 text-sm leading-relaxed text-ink-3">{pick(f.significance_hi, f.significance)}</p>}
              <div className="mt-3 flex flex-wrap gap-2">
                {cities.slice(0, 6).map((c) => (
                  <Link key={c.slug} to={`/muhurta/${f.slug}/${c.slug}`} data-testid={`muhurta-link-${f.slug}-${c.slug}`}
                    className="rounded-full border border-line px-3 py-1 text-xs text-ink-2 transition-colors hover:border-saffron hover:text-saffron">{c.name}</Link>
                ))}
                <Link to={`/muhurta/${f.slug}`} className="rounded-full border border-dashed border-line px-3 py-1 text-xs text-saffron">{pick("सभी नगर →", "All cities →")}</Link>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
